import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ShareDataWrapperContext } from '../..';
import { UnityOverlay, useSharedUnityMap } from '../SharedUnityMapLayer';
import LeftPanel from './components/LeftPanel';
import MapView from './components/MapView';
import RightPanel from './components/RightPanel';
import AreaDataModal from './components/AreaDataModal';
import CenterTopCards from './components/CenterTopCards';
import styles from './index.module.scss';
import type { WgqMapMode } from '../..';
import { clearUnityBusinessCleanupPayloads, setUnityBusinessCleanupPayloads } from '../UnityRenderMap/businessCleanup';
import type { RenderStreamingVideoElement, UnityClickDetail } from '../UnityRenderMap';
import { getUnityPosition, notEmpty } from '../UnityRenderMap/utils';

type EnvironmentMonitorPoint = {
  name: string;
  pointLng: string;
  pointLat: string;
  deviceId: number;
  waterMeterType: 'inlet' | 'vocs';
  positionOffset?: [number, number, number];
};

const ENVIRONMENT_MONITOR_PARENT = 'watermeter';
const ENVIRONMENT_MONITOR_LABEL_PARENT = 'WaterMeterLabel';
const ENVIRONMENT_MONITOR_PREFAB = 'Prefabs/watermeter';
const ENVIRONMENT_MONITOR_LABEL_PREFAB = 'Prefabs/WaterMeterLabelRoot';
const ENVIRONMENT_MONITOR_DEFAULT_STATUS = '开';
const UNITY_WATER_METER_TYPE_MAP: Record<EnvironmentMonitorPoint['waterMeterType'], string> = {
  inlet: '排污入口',
  vocs: 'vocs',
};

const ENVIRONMENT_MONITOR_POINTS: EnvironmentMonitorPoint[] = [
  { name: '涂装一期A跨VOCS', pointLng: '121.627772', pointLat: '31.338629', deviceId: 3453, waterMeterType: 'vocs' },
  { name: '涂装一期C跨VOCS', pointLng: '121.628126', pointLat: '31.339176', deviceId: 3452, waterMeterType: 'vocs' },
  { name: '涂装一期F跨VOCS', pointLng: '121.628615', pointLat: '31.339909', deviceId: 3448, waterMeterType: 'vocs' },
  { name: '涂装三期CD跨', pointLng: '121.629116', pointLat: '31.338672', deviceId: 3482, waterMeterType: 'vocs' },
  { name: '涂装三期AB跨', pointLng: '121.628851', pointLat: '31.338275', deviceId: 3805, waterMeterType: 'vocs' },
  { name: '涂装二期F跨', pointLng: '121.629692', pointLat: '31.336628', deviceId: 3451, waterMeterType: 'vocs' },
  { name: '涂装二期E跨', pointLng: '121.629329', pointLat: '31.336773', deviceId: 3450, waterMeterType: 'vocs' },
  {
    name: '4.5M钢板预处理VOCS排口',
    pointLng: '121.625780',
    pointLat: '31.346330',
    deviceId: 3818,
    waterMeterType: 'vocs',
    positionOffset: [0, 60, 0],
  },
  {
    name: '3M型材预处理VOCS排口',
    pointLng: '121.625520',
    pointLat: '31.346050',
    deviceId: 3818,
    waterMeterType: 'vocs',
    positionOffset: [0, 60, 0],
  },

  { name: '薄板中心预处理VOCS', pointLng: '121.639381', pointLat: '31.341559', deviceId: 3449, waterMeterType: 'vocs' },
  {
    name: '废水环保监测',
    pointLng: '121.624900',
    pointLat: '31.345720',
    deviceId: 3793,
    waterMeterType: 'inlet',
    positionOffset: [0, 80, 0],
  },
];

const getEnvironmentMonitorInstanceName = (point: EnvironmentMonitorPoint) =>
  `environment-monitor-${point.deviceId}-${point.name}`;

const createEnvironmentMonitorLabelText = (point: EnvironmentMonitorPoint) =>
  `<align=center><color=#52E7FF>${point.name}</color></align>`;

const createEnvironmentMonitorPayload = (
  videoElement?: RenderStreamingVideoElement,
  includeAdd = true,
  removeList: EnvironmentMonitorPoint[] = ENVIRONMENT_MONITOR_POINTS
) => {
  const instances = includeAdd
    ? ENVIRONMENT_MONITOR_POINTS.map(point => {
        const unityPosition = getUnityPosition(point, videoElement, '[UnityEnvironmentMap]');
        if (!unityPosition) {
          return null;
        }

        const position = point.positionOffset
          ? (unityPosition.map((value, index) => value + point.positionOffset![index]) as [number, number, number])
          : unityPosition;
        const name = getEnvironmentMonitorInstanceName(point);
        return {
          name,
          position,
          rotation: [0, 0, 0] as [number, number, number],
          grounded: false,
          parent: ENVIRONMENT_MONITOR_PARENT,
          properties: {
            ...point,
            name,
            labelType: 'environmentMonitor',
            waterMeterCurrentStatus: ENVIRONMENT_MONITOR_DEFAULT_STATUS,
            waterMeterType: UNITY_WATER_METER_TYPE_MAP[point.waterMeterType],
            waterMeterTypeCode: point.waterMeterType,
            waterMeterMsgs: [],
            key1: createEnvironmentMonitorLabelText(point),
          },
        };
      }).filter(notEmpty)
    : [];

  const remove =
    removeList.length > 0
      ? [
          {
            path: ENVIRONMENT_MONITOR_PREFAB,
            type: 'prefab' as const,
            instances: removeList.map(point => ({
              name: getEnvironmentMonitorInstanceName(point),
              parent: ENVIRONMENT_MONITOR_PARENT,
            })),
          },
          {
            path: ENVIRONMENT_MONITOR_LABEL_PREFAB,
            type: 'prefab' as const,
            instances: removeList.map(point => ({
              name: getEnvironmentMonitorInstanceName(point),
              parent: ENVIRONMENT_MONITOR_LABEL_PARENT,
            })),
          },
        ]
      : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove,
      add:
        instances.length > 0
          ? [
              {
                path: ENVIRONMENT_MONITOR_PREFAB,
                type: 'prefab' as const,
                instances,
                label: {
                  path: ENVIRONMENT_MONITOR_LABEL_PREFAB,
                  height: 70,
                  parent: ENVIRONMENT_MONITOR_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

const EnvironmentUnityMarkers = () => {
  const { unityReady, callUnity, videoElement } = useSharedUnityMap();
  const lastSyncSignatureRef = useRef('');

  const syncEnvironmentMonitorPoints = useCallback(async () => {
    if (!unityReady || !callUnity || !videoElement) return;

    const payload = createEnvironmentMonitorPayload(videoElement);
    const signature = JSON.stringify(payload.data);
    if (lastSyncSignatureRef.current === signature) return;

    await callUnity(payload);

    lastSyncSignatureRef.current = signature;
    setUnityBusinessCleanupPayloads('environmentMonitor', [createEnvironmentMonitorPayload(undefined, false)]);
  }, [callUnity, unityReady, videoElement]);

  useEffect(() => {
    syncEnvironmentMonitorPoints().catch(() => {
      clearUnityBusinessCleanupPayloads('environmentMonitor');
    });
  }, [syncEnvironmentMonitorPoints]);

  return null;
};

const UnityEnvironmentMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const { registerUnityClickHandler } = useSharedUnityMap();
  const [areaDataOpen, setAreaDataOpen] = useState(false);
  const [selectedAreaName, setSelectedAreaName] = useState('');
  const showDashboardPanels = !isStandardSize || !loadMap;

  const handleUnityClick = useCallback((detail: UnityClickDetail) => {
    const clickData = (detail?.data ?? {}) as Record<string, any>;
    const properties = clickData.properties?.properties ?? clickData.properties ?? clickData;
    if (properties?.labelType === 'environmentMonitor') return;

    const areaName = String(properties?.areaName ?? properties?.zoneName ?? properties?.name ?? '').trim();
    if (!areaName) return;

    setSelectedAreaName(areaName);
    setAreaDataOpen(true);
  }, []);

  useEffect(() => registerUnityClickHandler(handleUnityClick), [handleUnityClick, registerUnityClickHandler]);

  return (
    <div className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}>
      {mapMode === 'unityMap' && loadMap && <UnityOverlay className={styles.map} />}
      {mapMode === 'unityMap' && loadMap && <EnvironmentUnityMarkers />}
      {mapMode === 'mapView' && loadMap && <MapView className={styles.map} />}
      {showDashboardPanels && (
        <CenterTopCards className={loadMap ? styles.center_top_cards : styles.center_top_cards_no_map} />
      )}
      {showDashboardPanels && (
        <div
          className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}
          style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
        >
          <div className={classNames(styles.panel, !loadMap ? styles.noMapWidth : '')}>
            <LeftPanel />
          </div>
          <div className={classNames(styles.panel, !loadMap ? styles.noMapWidth : '')}>
            <RightPanel />
          </div>
        </div>
      )}
      <AreaDataModal open={areaDataOpen} areaName={selectedAreaName} onClose={() => setAreaDataOpen(false)} />
    </div>
  );
};

export default UnityEnvironmentMap;
